import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChartLineUp, Play, ListChecks, WarningCircle, CalendarBlank, VideoCamera, ClipboardText, Question, Lock, CaretRight } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const fmtDate = (iso) => {
  try {
    const d = new Date(iso.length <= 10 ? `${iso}T12:00:00` : iso);
    return d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" }) + (iso.length > 10 ? ` · ${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}` : "");
  } catch { return iso; }
};
const UP_ICON = { devoir: ClipboardText, quiz: Question, visio: VideoCamera };
const UP_LABEL = { devoir: "Devoir à rendre", quiz: "Quiz à passer", visio: "Visio" };

function Bar({ value, className = "bg-primary" }) {
  return (
    <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={value} aria-valuemin={0} aria-valuemax={100}>
      <div className={`h-full rounded-full transition-all ${className}`} style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
    </div>
  );
}

/** Petit résumé pour la page d'accueil de l'étudiant. */
export function ProgressSummaryCard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/my-progress").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data || data.totals.courses === 0) return null;
  const t = data.totals;
  const nextUp = data.upcoming[0];
  return (
    <button type="button" onClick={() => navigate("/app/ma-progression")} data-testid="progress-summary"
      className="group w-full rounded-xl border border-border bg-card p-5 text-left transition-colors hover:border-primary">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2"><ChartLineUp size={22} weight="fill" className="text-primary" /><h3 className="font-display text-lg font-bold">Ma progression</h3></div>
        <span className="flex items-center gap-1 text-xs font-medium text-primary">Détails <CaretRight size={14} className="transition-transform group-hover:translate-x-1" /></span>
      </div>
      <div className="mt-3 flex items-end gap-3">
        <span className="font-display text-3xl font-extrabold">{t.percent}%</span>
        <span className="pb-1 text-sm text-muted-foreground">des séquences terminées</span>
      </div>
      <div className="mt-2"><Bar value={t.percent} /></div>
      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground">
        {t.checks_total > 0 && <span><ListChecks size={14} className="mr-1 inline" />Contrôles réussis : <b className="text-foreground">{t.checks_passed}/{t.checks_total}</b></span>}
        {t.weak_count > 0 && <span className="text-amber-700 dark:text-amber-400"><WarningCircle size={14} className="mr-1 inline" />{t.weak_count} point{t.weak_count > 1 ? "s" : ""} à revoir</span>}
        {nextUp && <span><CalendarBlank size={14} className="mr-1 inline" />{UP_LABEL[nextUp.type]} : {fmtDate(nextUp.date)}</span>}
      </div>
    </button>
  );
}

export default function MaProgression() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/my-progress").then((r) => setData(r.data)).catch(() => setData({ courses: [], upcoming: [], totals: {} })); }, []);
  if (!data) return <div className="text-muted-foreground">Chargement…</div>;
  const t = data.totals || {};
  const go = (cid, lid) => navigate(`/app/cours/${cid}${lid ? `?sequence=${lid}` : ""}`);

  return (
    <div className="space-y-6" data-testid="progress-page">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Mon parcours</p>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Ma progression</h1>
        <p className="text-sm text-muted-foreground">Où vous en êtes dans chaque cours, ce qu'il faut revoir et ce qui arrive.</p>
      </div>

      {data.courses.length === 0 && <p className="text-sm text-muted-foreground">Aucun cours disponible pour le moment.</p>}

      {data.courses.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { label: "Séquences terminées", value: `${t.percent}%` },
            { label: "Contrôles réussis", value: t.checks_total ? `${t.checks_passed}/${t.checks_total}` : "—" },
            { label: "Points à revoir", value: t.weak_count ?? 0 },
            { label: "Cours suivis", value: t.courses },
          ].map((x) => (
            <div key={x.label} className="rounded-xl border border-border bg-card p-4">
              <p className="font-display text-2xl font-extrabold">{x.value}</p>
              <p className="text-xs text-muted-foreground">{x.label}</p>
            </div>
          ))}
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-4">
          {data.courses.map((c) => (
            <section key={c.id} className="rounded-xl border border-border bg-card p-5" data-testid={`progress-course-${c.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="font-display text-lg font-bold leading-tight">{c.title}</h2>
                  <p className="text-xs text-muted-foreground">
                    {c.lessons_done}/{c.lessons_total} séquences terminées
                    {c.checks_total > 0 && ` · contrôles réussis ${c.checks_passed}/${c.checks_total}`}
                    {c.avg_pct !== null && ` · moyenne ${c.avg_pct} %`}
                  </p>
                </div>
                <span className="font-display text-2xl font-extrabold">{c.percent}%</span>
              </div>
              <div className="mt-3"><Bar value={c.percent} className={c.percent === 100 ? "bg-emerald-600" : "bg-primary"} /></div>

              {c.sequential_order && c.locked_count > 0 && (
                <p className="mt-3 flex items-center gap-1 text-xs text-muted-foreground"><Lock size={13} /> Ce cours s'ouvre dans l'ordre : {c.locked_count} séquence{c.locked_count > 1 ? "s" : ""} encore verrouillée{c.locked_count > 1 ? "s" : ""}.</p>
              )}

              {c.next_lesson && (
                <div className="mt-4">
                  <Button className="w-full justify-start bg-primary hover:bg-primary/90 sm:w-auto" onClick={() => go(c.id, c.next_lesson.id)} data-testid={`progress-next-${c.id}`}>
                    <Play size={16} weight="fill" className="mr-2 shrink-0" />
                    <span className="truncate">Continuer : {c.next_lesson.title}</span>
                  </Button>
                </div>
              )}
              {!c.next_lesson && c.lessons_total > 0 && c.percent === 100 && <p className="mt-3 text-sm font-medium text-emerald-700">Cours terminé, bravo !</p>}

              {c.weak.length > 0 && (
                <div className="mt-4 rounded-lg bg-amber-50 p-3 dark:bg-amber-950/30" data-testid={`progress-weak-${c.id}`}>
                  <p className="mb-1 flex items-center gap-1 text-sm font-semibold"><WarningCircle size={16} weight="fill" className="text-amber-600" /> À revoir</p>
                  {c.weak.map((w) => (
                    <button key={w.lesson_id} type="button" onClick={() => go(c.id, w.lesson_id)} className="flex w-full items-center justify-between gap-3 rounded px-2 py-1.5 text-left text-sm hover:bg-white/60 dark:hover:bg-white/10">
                      <span className="min-w-0 truncate">{w.title}</span>
                      <span className="shrink-0 text-xs text-muted-foreground">{w.best_pct} % (seuil {w.pass_pct} %)</span>
                    </button>
                  ))}
                </div>
              )}

              {c.todo_checks.length > 0 && (
                <div className="mt-3">
                  <p className="mb-1 flex items-center gap-1 text-sm font-semibold"><ListChecks size={16} className="text-primary" /> Contrôles à passer</p>
                  {c.todo_checks.map((w) => (
                    <button key={w.lesson_id} type="button" onClick={() => go(c.id, w.lesson_id)} className="flex w-full items-center justify-between gap-3 rounded px-2 py-1.5 text-left text-sm hover:bg-muted">
                      <span className="min-w-0 truncate">{w.title}</span>
                      {w.required && <span className="shrink-0 text-xs text-accent">Obligatoire</span>}
                    </button>
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>

        <aside className="space-y-3">
          <div className="rounded-xl border border-border bg-card p-5" data-testid="progress-upcoming">
            <h3 className="mb-3 flex items-center gap-2 font-display text-base font-bold"><CalendarBlank size={18} className="text-primary" /> À venir</h3>
            {data.upcoming.length === 0 && <p className="text-sm text-muted-foreground">Rien de prévu pour le moment.</p>}
            <div className="space-y-2">
              {data.upcoming.map((u, i) => {
                const Icon = UP_ICON[u.type] || CalendarBlank;
                return (
                  <button key={i} type="button" onClick={() => navigate(u.link)} className="flex w-full items-start gap-3 rounded-lg border border-border p-3 text-left hover:bg-secondary">
                    <Icon size={18} className="mt-0.5 shrink-0 text-primary" />
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium">{u.title}</span>
                      <span className="block text-xs text-muted-foreground">{UP_LABEL[u.type]} · {fmtDate(u.date)}</span>
                      <span className="block truncate text-xs text-muted-foreground">{u.course_title}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
